/**
 * Landing Page
 * Public-facing page for new visitors to learn about Mindora
 */

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Mic, Shield, Globe, BookOpen, MessageSquare, BarChart3,
  ShieldCheck, ShieldAlert, Lock, Eye, Ban,
  CheckCircle2, XCircle, AlertTriangle, Fingerprint,
  GraduationCap, Clock, Filter, Volume2, Bell,
  Activity, Users, Send, Check, Crown,
} from 'lucide-react';
import { PLAN_LIMITS } from '@kids-lms/constants';
import type { SubscriptionPlan, BillingInterval } from '@kids-lms/types';

/* ------------------------------------------------------------------ */
/*  Animated Phone Chat Demo                                          */
/* ------------------------------------------------------------------ */

const DEMO_MESSAGES = [
  { role: 'user' as const, text: 'What is the solar system?', delay: 800 },
  {
    role: 'assistant' as const,
    text: "Whoosh! Great question! The solar system is like a big family in space! The Sun is at the center, and 8 planets orbit around it — like a giant space dance! 🌟",
    delay: 1800,
  },
  { role: 'user' as const, text: 'Which planet is biggest?', delay: 2000 },
  {
    role: 'assistant' as const,
    text: "Ka-pow! Jupiter is the biggest! It's SO big you could fit more than 1,000 Earths inside it! Imagine a basketball — that's Jupiter. A tiny pea next to it — that's Earth! ⚡",
    delay: 1800,
  },
];

function PhoneMockup() {
  const [visibleMessages, setVisibleMessages] = useState<number>(0);
  const [isTyping, setIsTyping] = useState(false);

  useEffect(() => {
    if (visibleMessages >= DEMO_MESSAGES.length) {
      // Reset after a pause
      const timer = setTimeout(() => {
        setVisibleMessages(0);
      }, 5000);
      return () => clearTimeout(timer);
    }

    const nextMsg = DEMO_MESSAGES[visibleMessages];
    if (nextMsg.role === 'assistant') {
      // Show typing indicator first
      setIsTyping(true);
      const typingTimer = setTimeout(() => {
        setIsTyping(false);
        setVisibleMessages((v) => v + 1);
      }, nextMsg.delay);
      return () => clearTimeout(typingTimer);
    } else {
      const timer = setTimeout(() => {
        setVisibleMessages((v) => v + 1);
      }, nextMsg.delay);
      return () => clearTimeout(timer);
    }
  }, [visibleMessages]);

  return (
    <div className="relative mx-auto" style={{ maxWidth: 320 }}>
      {/* Phone frame */}
      <div className="relative bg-gray-900 rounded-[3rem] p-3 shadow-2xl">
        {/* Notch */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-32 h-7 bg-gray-900 rounded-b-2xl z-10" />
        {/* Screen */}
        <div className="bg-white rounded-[2.25rem] overflow-hidden" style={{ minHeight: 520 }}>
          {/* App header */}
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 px-5 pt-10 pb-3">
            <div className="flex items-center gap-3">
              <img src="/companion-spark.png" alt="Spark" className="w-9 h-9 rounded-xl bg-white/20 p-0.5" />
              <div>
                <p className="text-white font-bold text-sm">⚡ Spark</p>
                <p className="text-blue-100 text-[10px]">Science &middot; Explore Mode</p>
              </div>
              <div className="ml-auto flex items-center gap-1 bg-white/20 rounded-lg px-2 py-1">
                <Volume2 className="w-3 h-3 text-white" />
                <span className="text-white text-[10px] font-medium">Auto</span>
              </div>
            </div>
          </div>

          {/* Chat area */}
          <div className="px-4 py-3 space-y-3" style={{ minHeight: 360 }}>
            <AnimatePresence>
              {DEMO_MESSAGES.slice(0, visibleMessages).map((msg, i) => (
                <motion.div
                  key={`${msg.text}-${i}`}
                  initial={{ opacity: 0, y: 12, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  transition={{ duration: 0.3 }}
                  className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[85%] px-3.5 py-2.5 text-[12px] leading-relaxed ${
                      msg.role === 'user'
                        ? 'bg-blue-500 text-white rounded-2xl rounded-tr-md'
                        : 'bg-gray-100 text-gray-800 rounded-2xl rounded-tl-md'
                    }`}
                  >
                    {msg.text}
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>

            {/* Typing indicator */}
            <AnimatePresence>
              {isTyping && (
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  className="flex justify-start"
                >
                  <div className="bg-gray-100 rounded-2xl rounded-tl-md px-4 py-3 flex gap-1">
                    <motion.span
                      className="w-2 h-2 bg-gray-400 rounded-full"
                      animate={{ opacity: [0.4, 1, 0.4] }}
                      transition={{ repeat: Infinity, duration: 1, delay: 0 }}
                    />
                    <motion.span
                      className="w-2 h-2 bg-gray-400 rounded-full"
                      animate={{ opacity: [0.4, 1, 0.4] }}
                      transition={{ repeat: Infinity, duration: 1, delay: 0.2 }}
                    />
                    <motion.span
                      className="w-2 h-2 bg-gray-400 rounded-full"
                      animate={{ opacity: [0.4, 1, 0.4] }}
                      transition={{ repeat: Infinity, duration: 1, delay: 0.4 }}
                    />
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Input bar */}
          <div className="px-3 pb-4 pt-1 border-t border-gray-100">
            <div className="flex items-center gap-2">
              <motion.div
                className="w-10 h-10 bg-red-500 rounded-xl flex items-center justify-center flex-shrink-0"
                animate={visibleMessages < DEMO_MESSAGES.length && DEMO_MESSAGES[visibleMessages]?.role === 'user'
                  ? { scale: [1, 1.15, 1], boxShadow: ['0 0 0 0 rgba(239,68,68,0)', '0 0 0 8px rgba(239,68,68,0.2)', '0 0 0 0 rgba(239,68,68,0)'] }
                  : {}}
                transition={{ repeat: Infinity, duration: 1.5 }}
              >
                <Mic className="w-5 h-5 text-white" />
              </motion.div>
              <div className="flex-1 bg-gray-50 rounded-xl px-3 py-2.5 text-[11px] text-gray-400">
                Tap mic to speak...
              </div>
              <div className="w-9 h-9 bg-blue-500 rounded-xl flex items-center justify-center flex-shrink-0">
                <Send className="w-4 h-4 text-white" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Mock Parent Dashboard                                             */
/* ------------------------------------------------------------------ */

function ParentDashboardMockup() {
  const barHeights = [35, 55, 45, 70, 60, 80, 50];
  const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

  return (
    <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
      {/* Dashboard header */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/logo.png" alt="Mindora" className="w-8 h-8" />
            <span className="text-white font-bold">Parent Dashboard</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center">
              <Bell className="w-4 h-4 text-white" />
            </div>
          </div>
        </div>
      </div>

      <div className="p-5">
        {/* Stats row */}
        <div className="grid grid-cols-3 gap-3 mb-5">
          {[
            { label: 'Sessions Today', value: '3', icon: <Activity className="w-4 h-4 text-blue-500" />, bg: 'bg-blue-50' },
            { label: 'Subjects', value: '4', icon: <BookOpen className="w-4 h-4 text-purple-500" />, bg: 'bg-purple-50' },
            { label: 'Time Used', value: '28m', icon: <Clock className="w-4 h-4 text-green-500" />, bg: 'bg-green-50' },
          ].map((stat) => (
            <div key={stat.label} className={`${stat.bg} rounded-xl p-3 text-center`}>
              <div className="flex justify-center mb-1">{stat.icon}</div>
              <p className="text-xl font-bold text-gray-900">{stat.value}</p>
              <p className="text-[10px] text-gray-500">{stat.label}</p>
            </div>
          ))}
        </div>

        {/* Activity chart */}
        <div className="mb-5">
          <div className="flex items-center justify-between mb-3">
            <p className="text-sm font-semibold text-gray-900">Weekly Activity</p>
            <p className="text-xs text-gray-400">Minutes/day</p>
          </div>
          <div className="flex items-end justify-between gap-1.5 h-24 px-1">
            {barHeights.map((h, i) => (
              <div key={days[i]} className="flex-1 flex flex-col items-center gap-1">
                <motion.div
                  className="w-full bg-gradient-to-t from-blue-500 to-purple-400 rounded-t-md"
                  initial={{ height: 0 }}
                  whileInView={{ height: `${h}%` }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1, duration: 0.5 }}
                />
                <span className="text-[9px] text-gray-400">{days[i]}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Child profiles */}
        <div>
          <p className="text-sm font-semibold text-gray-900 mb-3">Children</p>
          <div className="space-y-2">
            {[
              { name: 'Emma', age: 8, grade: 3, img: '/avatar-girl.png', subjects: 'Science, Math', status: 'Online', statusColor: 'bg-green-400' },
              { name: 'Liam', age: 11, grade: 6, img: '/avatar-boy.png', subjects: 'History, English', status: 'Offline', statusColor: 'bg-gray-300' },
            ].map((child) => (
              <div key={child.name} className="flex items-center gap-3 p-2.5 rounded-xl bg-gray-50 hover:bg-blue-50 transition-colors">
                <div className="relative">
                  <img src={child.img} alt={child.name} className="w-10 h-10 rounded-xl" />
                  <div className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 ${child.statusColor} rounded-full border-2 border-white`} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-semibold text-gray-900">{child.name}</p>
                    <span className="text-[10px] text-gray-400">Age {child.age}, Grade {child.grade}</span>
                  </div>
                  <p className="text-[11px] text-gray-500 truncate">{child.subjects}</p>
                </div>
                <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0" />
              </div>
            ))}
          </div>
        </div>

        {/* Recent flagged */}
        <div className="mt-4 p-3 bg-amber-50 rounded-xl border border-amber-100">
          <div className="flex items-center gap-2 mb-1">
            <AlertTriangle className="w-3.5 h-3.5 text-amber-500" />
            <p className="text-xs font-semibold text-amber-700">Flagged Content</p>
          </div>
          <p className="text-[11px] text-amber-600">No flagged conversations this week</p>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Animation Variants                                                */
/* ------------------------------------------------------------------ */

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.1, duration: 0.5 },
  }),
};

const fadeIn = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.6 } },
};

/* ------------------------------------------------------------------ */
/*  Pricing Section                                                   */
/* ------------------------------------------------------------------ */

const PLAN_FEATURES: Record<SubscriptionPlan, string[]> = {
  free: [
    '10 messages per day',
    '1 child profile',
    'All AI companions',
    'All learning modes',
    'Voice input & TTS',
    'Daily habits & challenges',
  ],
  starter: [
    '40 messages per day',
    '1 child profile',
    'All AI companions',
    'All learning modes',
    'Voice input & TTS',
    'Daily habits & challenges',
    'Detailed analytics',
  ],
  pro: [
    '80 messages per day',
    '1 child profile',
    'All AI companions',
    'All learning modes',
    'Voice input & TTS',
    'Daily habits & challenges',
    'Detailed analytics',
    'Priority support',
  ],
  family: [
    '50 messages per day per child',
    'Up to 4 children',
    'All AI companions',
    'All learning modes',
    'Voice input & TTS',
    'Daily habits & challenges',
    'Detailed analytics',
    'Priority support',
  ],
};

function PricingSection() {
  const [interval, setInterval] = useState<BillingInterval>('monthly');

  const plans: SubscriptionPlan[] = ['free', 'starter', 'pro', 'family'];

  return (
    <section id="pricing" className="py-20 px-4 bg-gray-50">
      <div className="max-w-6xl mx-auto">
        <motion.div
          className="text-center mb-12"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeIn}
        >
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">
            Simple, Transparent Pricing
          </h2>
          <p className="mt-4 text-lg text-gray-600 max-w-2xl mx-auto">
            Every plan includes all features. Choose based on how much your child learns each day.
          </p>

          {/* Billing toggle */}
          <div className="mt-8 inline-flex items-center bg-white rounded-xl p-1 shadow-sm border border-gray-200">
            <button
              onClick={() => setInterval('monthly')}
              className={`px-5 py-2 text-sm font-medium rounded-lg transition-colors ${
                interval === 'monthly'
                  ? 'bg-blue-600 text-white shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setInterval('annual')}
              className={`px-5 py-2 text-sm font-medium rounded-lg transition-colors ${
                interval === 'annual'
                  ? 'bg-blue-600 text-white shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Annual
              <span className={`ml-1.5 text-xs font-bold ${interval === 'annual' ? 'text-blue-100' : 'text-green-600'}`}>
                Save 33%
              </span>
            </button>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {plans.map((plan, i) => {
            const limits = PLAN_LIMITS[plan];
            const features = PLAN_FEATURES[plan];
            const price = interval === 'monthly'
              ? limits.monthlyPrice / 100
              : limits.annualPrice / 100 / 12;
            const isPopular = plan === 'pro';
            const isFree = plan === 'free';

            return (
              <motion.div
                key={plan}
                className={`relative bg-white rounded-2xl p-6 border-2 transition-shadow hover:shadow-lg ${
                  isPopular
                    ? 'border-purple-400 shadow-lg shadow-purple-100'
                    : 'border-gray-100'
                }`}
                custom={i}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeUp}
              >
                {isPopular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span className="bg-gradient-to-r from-purple-600 to-blue-600 text-white text-xs font-bold px-4 py-1 rounded-full flex items-center gap-1">
                      <Crown className="w-3 h-3" />
                      MOST POPULAR
                    </span>
                  </div>
                )}

                <h3 className="text-lg font-bold text-gray-900">{limits.displayName}</h3>

                <div className="mt-4">
                  {isFree ? (
                    <div className="flex items-baseline gap-1">
                      <span className="text-4xl font-extrabold text-gray-900">$0</span>
                      <span className="text-gray-500">/forever</span>
                    </div>
                  ) : (
                    <div className="flex items-baseline gap-1">
                      <span className="text-4xl font-extrabold text-gray-900">
                        ${price.toFixed(2)}
                      </span>
                      <span className="text-gray-500">/mo</span>
                    </div>
                  )}
                  {!isFree && interval === 'annual' && (
                    <p className="text-xs text-green-600 mt-1">
                      ${(limits.annualPrice / 100).toFixed(2)} billed annually
                    </p>
                  )}
                </div>

                <Link
                  to="/register"
                  className={`mt-6 block w-full py-3 text-center text-sm font-semibold rounded-xl transition-all ${
                    isFree
                      ? 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      : isPopular
                      ? 'bg-gradient-to-r from-purple-600 to-blue-600 text-white hover:shadow-lg hover:shadow-purple-200'
                      : 'bg-blue-600 text-white hover:bg-blue-700'
                  }`}
                >
                  {isFree ? 'Get Started Free' : 'Start Now'}
                </Link>

                <ul className="mt-6 space-y-3">
                  {features.map((feat) => (
                    <li key={feat} className="flex items-start gap-2 text-sm text-gray-600">
                      <Check className={`w-4 h-4 mt-0.5 flex-shrink-0 ${isPopular ? 'text-purple-500' : 'text-green-500'}`} />
                      {feat}
                    </li>
                  ))}
                </ul>
              </motion.div>
            );
          })}
        </div>

        <p className="text-center text-sm text-gray-500 mt-8">
          All plans include COPPA-compliant safety, content moderation, and parent dashboard access.
        </p>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/*  Landing Page                                                      */
/* ------------------------------------------------------------------ */

export default function LandingPage() {
  // SEO: Set document title and meta tags
  useEffect(() => {
    document.title = 'Mindora - AI Tutor for Kids | Safe, Voice-Powered Learning for Ages 5-18';
    const setMeta = (name: string, content: string) => {
      let el = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        el.name = name;
        document.head.appendChild(el);
      }
      el.content = content;
    };
    setMeta('description', 'Mindora is a COPPA-compliant AI tutor built for kids ages 5-18. Voice-powered learning with parent controls, content moderation, and age-adapted responses. Free plan available.');
    setMeta('keywords', 'AI tutor for kids, children learning app, safe AI for children, COPPA compliant, voice learning, educational AI, kids education app, Mindora');
    // Open Graph
    const setOG = (prop: string, content: string) => {
      let el = document.querySelector(`meta[property="${prop}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute('property', prop);
        document.head.appendChild(el);
      }
      el.content = content;
    };
    setOG('og:title', 'Mindora - Safe AI Tutor for Kids');
    setOG('og:description', 'Voice-powered AI learning companion for children ages 5-18. COPPA compliant with full parent controls.');
    setOG('og:type', 'website');

    // Capture referral code from URL
    const params = new URLSearchParams(window.location.search);
    const ref = params.get('ref');
    if (ref) {
      localStorage.setItem('mindora_ref', ref);
    }

    // Fetch testimonials
    const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';
    fetch(`${apiUrl}/testimonials`).then(r => r.json()).then(res => {
      if (res.success && res.data?.length > 0) setTestimonials(res.data);
    }).catch(() => {});
  }, []);

  const DEFAULT_TESTIMONIALS = [
    { name: 'Sarah M.', roleDescription: 'Parent of an 8-year-old', quote: "My 8-year-old loves chatting with Spark! She asks questions I could never answer, and Mindora explains things perfectly for her age.", imageUrl: null, rating: 5 },
    { name: 'Ahmed K.', roleDescription: 'Parent of 6 and 10-year-old', quote: "Finally an AI app I trust with my kids. The safety controls and content moderation give me real peace of mind.", imageUrl: null, rating: 5 },
    { name: 'Priya R.', roleDescription: 'Parent of a 12-year-old', quote: "Mindora helped my daughter improve her math grades. She actually enjoys studying now — the daily challenges keep her motivated!", imageUrl: null, rating: 5 },
  ];
  const [testimonials, setTestimonials] = useState(DEFAULT_TESTIMONIALS);

  // Contact form state
  const [contactForm, setContactForm] = useState({ name: '', email: '', subject: 'General Inquiry', message: '' });
  const [contactLoading, setContactLoading] = useState(false);
  const [contactStatus, setContactStatus] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const handleContactSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setContactLoading(true);
    setContactStatus(null);
    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';
      const res = await fetch(`${apiUrl}/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(contactForm),
      });
      const data = await res.json();
      if (data.success) {
        setContactStatus({ type: 'success', text: 'Thank you! We\'ll get back to you soon.' });
        setContactForm({ name: '', email: '', subject: 'General Inquiry', message: '' });
      } else {
        setContactStatus({ type: 'error', text: data.error?.message || 'Something went wrong. Please try again.' });
      }
    } catch {
      setContactStatus({ type: 'error', text: 'Network error. Please try again later.' });
    } finally {
      setContactLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Nav */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md border-b border-gray-100">
        <div className="max-w-6xl mx-auto px-4 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src="/logo.png" alt="Mindora" className="w-10 h-10 object-contain" />
            <span className="text-xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              Mindora
            </span>
          </Link>
          <div className="flex items-center gap-3">
            <a
              href="#safety"
              className="hidden sm:inline px-4 py-2 text-sm font-medium text-gray-500 hover:text-blue-600 transition-colors"
            >
              Safety
            </a>
            <a
              href="#pricing"
              className="hidden sm:inline px-4 py-2 text-sm font-medium text-gray-500 hover:text-blue-600 transition-colors"
            >
              Pricing
            </a>
            <a
              href="#how-it-works"
              className="hidden sm:inline px-4 py-2 text-sm font-medium text-gray-500 hover:text-blue-600 transition-colors"
            >
              How It Works
            </a>
            <a
              href="#contact"
              className="hidden sm:inline px-4 py-2 text-sm font-medium text-gray-500 hover:text-blue-600 transition-colors"
            >
              Contact
            </a>
            <Link
              to="/login"
              className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-blue-600 transition-colors"
            >
              Log In
            </Link>
            <Link
              to="/register"
              className="px-5 py-2.5 text-sm font-medium text-white bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl hover:shadow-lg hover:shadow-blue-500/25 transition-all"
            >
              Get Started Free
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="pt-24 pb-16 px-4 overflow-hidden">
        <div className="max-w-6xl mx-auto flex flex-col lg:flex-row items-center gap-10">
          <motion.div
            className="flex-1 text-center lg:text-left"
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-green-50 border border-green-200 rounded-full text-green-700 text-sm font-medium mb-6">
              <ShieldCheck className="w-4 h-4" />
              COPPA Compliant &middot; Built for Children
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-gray-900 leading-tight">
              The AI Tutor
              <span className="block bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                Built for Kids, Not Adults
              </span>
            </h1>
            <p className="mt-6 text-lg sm:text-xl text-gray-600 max-w-xl mx-auto lg:mx-0">
              Mindora is a voice-powered AI learning companion with real safety controls. Every message is filtered, every response is age-appropriate, and parents see everything.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
              <Link
                to="/register"
                className="px-8 py-4 text-lg font-semibold text-white bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl hover:shadow-xl hover:shadow-blue-500/25 transition-all text-center"
              >
                Get Started Free
              </Link>
              <a
                href="#safety"
                className="px-8 py-4 text-lg font-semibold text-gray-700 bg-gray-100 rounded-2xl hover:bg-gray-200 transition-all text-center"
              >
                See Our Safety Controls
              </a>
            </div>
            <p className="mt-4 text-sm text-gray-400">No credit card required. Free for 10 messages/day.</p>
          </motion.div>

          {/* Animated Phone Mockup */}
          <motion.div
            className="flex-1 flex justify-center"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            <div className="relative">
              <div className="absolute -inset-10 bg-gradient-to-r from-blue-400/15 to-purple-400/15 rounded-full blur-3xl" />
              <PhoneMockup />
              {/* Floating badges */}
              <motion.div
                className="absolute -left-6 top-24 bg-white rounded-2xl shadow-lg px-3 py-2 flex items-center gap-2 z-10"
                animate={{ y: [0, -6, 0] }}
                transition={{ repeat: Infinity, duration: 3, ease: 'easeInOut' }}
              >
                <Shield className="w-4 h-4 text-green-500" />
                <span className="text-xs font-semibold text-gray-700">Input Filtered</span>
              </motion.div>
              <motion.div
                className="absolute -right-6 top-48 bg-white rounded-2xl shadow-lg px-3 py-2 flex items-center gap-2 z-10"
                animate={{ y: [0, -6, 0] }}
                transition={{ repeat: Infinity, duration: 3, ease: 'easeInOut', delay: 1 }}
              >
                <Eye className="w-4 h-4 text-blue-500" />
                <span className="text-xs font-semibold text-gray-700">Parent Monitored</span>
              </motion.div>
              <motion.div
                className="absolute -left-2 bottom-28 bg-white rounded-2xl shadow-lg px-3 py-2 flex items-center gap-2 z-10"
                animate={{ y: [0, -6, 0] }}
                transition={{ repeat: Infinity, duration: 3, ease: 'easeInOut', delay: 2 }}
              >
                <GraduationCap className="w-4 h-4 text-purple-500" />
                <span className="text-xs font-semibold text-gray-700">Age Adapted</span>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Why Not ChatGPT - Comparison */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="max-w-5xl mx-auto">
          <motion.div
            className="text-center mb-12"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeIn}
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">
              Why Not Just Use ChatGPT?
            </h2>
            <p className="mt-4 text-lg text-gray-600 max-w-2xl mx-auto">
              ChatGPT is built for adults. It has no age filters, no content moderation for children, and no parental oversight. Here's what Mindora does differently.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {/* ChatGPT column */}
            <motion.div
              className="bg-white rounded-2xl border-2 border-red-100 p-6"
              custom={0}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeUp}
            >
              <div className="flex items-center gap-3 mb-5">
                <div className="w-10 h-10 bg-red-50 rounded-xl flex items-center justify-center">
                  <XCircle className="w-6 h-6 text-red-400" />
                </div>
                <h3 className="text-lg font-bold text-gray-900">Regular AI Chatbots</h3>
              </div>
              <ul className="space-y-3">
                {[
                  'No age-appropriate content filtering',
                  'Can discuss any topic with a child',
                  'No input moderation or keyword blocking',
                  'No PII detection (child can share personal info)',
                  'Parents have zero visibility',
                  'Adult vocabulary and complexity',
                  'No daily usage limits',
                  'Terms of service require age 13+',
                ].map((item) => (
                  <li key={item} className="flex items-start gap-2.5 text-sm text-gray-600">
                    <XCircle className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </motion.div>

            {/* Mindora column */}
            <motion.div
              className="bg-white rounded-2xl border-2 border-green-100 p-6 relative"
              custom={1}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeUp}
            >
              <div className="absolute -top-3 right-6 bg-green-500 text-white text-xs font-bold px-3 py-1 rounded-full">
                CHILD SAFE
              </div>
              <div className="flex items-center gap-3 mb-5">
                <div className="w-10 h-10 bg-green-50 rounded-xl flex items-center justify-center">
                  <CheckCircle2 className="w-6 h-6 text-green-500" />
                </div>
                <h3 className="text-lg font-bold text-gray-900">Mindora</h3>
              </div>
              <ul className="space-y-3">
                {[
                  'Age-based content filtering (5-7, 8-10, 11-13, 14+)',
                  'Blocked topics auto-redirect to learning',
                  'Input moderation scans every message',
                  'PII detection blocks phone, email, address sharing',
                  'Full parent dashboard with conversation review',
                  'Vocabulary adapts to child\'s grade level',
                  'Configurable daily time & message limits',
                  'Built for kids from day one, COPPA compliant',
                ].map((item) => (
                  <li key={item} className="flex items-start gap-2.5 text-sm text-gray-600">
                    <CheckCircle2 className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Safety Controls - The Differentiator */}
      <section id="safety" className="py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <motion.div
            className="text-center mb-14"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeIn}
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">
              9 Layers of Safety Your Child Deserves
            </h2>
            <p className="mt-4 text-lg text-gray-600 max-w-2xl mx-auto">
              We don't just filter bad words. Mindora has a comprehensive safety system that protects your child at every step of every conversation.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                icon: <Filter className="w-6 h-6" />,
                title: 'Input Moderation',
                description: 'Every message your child sends is scanned for inappropriate content, dangerous topics, and blocked keywords before it reaches the AI.',
                color: 'blue',
              },
              {
                icon: <ShieldAlert className="w-6 h-6" />,
                title: 'Output Filtering',
                description: 'AI responses are checked for age-appropriateness before being shown. If anything slips through, it gets caught and replaced.',
                color: 'purple',
              },
              {
                icon: <Fingerprint className="w-6 h-6" />,
                title: 'PII Detection',
                description: 'If your child tries to share their phone number, email, home address, or other personal info, Mindora blocks it instantly and explains why.',
                color: 'red',
              },
              {
                icon: <GraduationCap className="w-6 h-6" />,
                title: 'Age-Based Responses',
                description: 'A 6-year-old and a 12-year-old get completely different vocabulary, complexity, and content boundaries. Four distinct age tiers.',
                color: 'green',
              },
              {
                icon: <Eye className="w-6 h-6" />,
                title: 'Parent Dashboard',
                description: 'Read every conversation. Get flagged content alerts. See learning progress. Set allowed subjects and blocked keywords per child.',
                color: 'orange',
              },
              {
                icon: <Ban className="w-6 h-6" />,
                title: 'Topic Boundaries',
                description: 'Parents set which subjects are allowed. If a child asks about something outside their allowed topics, Mindora redirects them to learning.',
                color: 'pink',
              },
              {
                icon: <Clock className="w-6 h-6" />,
                title: 'Daily Time Limits',
                description: 'Set how many minutes per day your child can use Mindora. When time is up, the session ends automatically.',
                color: 'teal',
              },
              {
                icon: <AlertTriangle className="w-6 h-6" />,
                title: 'Flagged Content Alerts',
                description: 'When the system detects concerning content, parents are notified immediately with severity levels: low, medium, high, or critical.',
                color: 'amber',
              },
              {
                icon: <Lock className="w-6 h-6" />,
                title: 'COPPA Compliance',
                description: 'Parental consent required for children under 13. No data selling. No ads. No third-party tracking. Your child\'s data stays private.',
                color: 'indigo',
              },
            ].map((item, i) => {
              const colorMap: Record<string, string> = {
                blue: 'bg-blue-50 text-blue-600',
                purple: 'bg-purple-50 text-purple-600',
                red: 'bg-red-50 text-red-600',
                green: 'bg-green-50 text-green-600',
                orange: 'bg-orange-50 text-orange-600',
                pink: 'bg-pink-50 text-pink-600',
                teal: 'bg-teal-50 text-teal-600',
                amber: 'bg-amber-50 text-amber-600',
                indigo: 'bg-indigo-50 text-indigo-600',
              };
              return (
                <motion.div
                  key={item.title}
                  className="bg-white rounded-2xl p-6 border border-gray-100 hover:shadow-lg hover:border-blue-100 transition-all"
                  custom={i}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeUp}
                >
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${colorMap[item.color]}`}>
                    {item.icon}
                  </div>
                  <h3 className="mt-4 text-lg font-bold text-gray-900">{item.title}</h3>
                  <p className="mt-2 text-gray-600 text-sm leading-relaxed">{item.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="py-20 px-4 bg-gray-50">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-14">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">How It Works</h2>
            <p className="mt-4 text-lg text-gray-600">Three steps to safer, smarter learning</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                step: '1',
                title: 'Parent Signs Up',
                desc: 'Create your account, add your child\'s profile with their age and grade, set allowed subjects and time limits.',
                img: '/parent_dashboard_bg.png',
              },
              {
                step: '2',
                title: 'Pick a Buddy & Subject',
                desc: 'Choose a learning companion — Spark, Sage, or Blaze — then pick a subject and learning mode.',
                img: '/math_bg.png',
              },
              {
                step: '3',
                title: 'Tap & Talk',
                desc: 'Your child taps the mic, asks a question by voice, and their AI buddy responds instantly at their level. No typing needed.',
                img: '/companion-spark.png',
              },
            ].map((item, i) => (
              <motion.div
                key={item.step}
                className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-lg transition-shadow"
                custom={i}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeUp}
              >
                <div className="h-44 bg-gradient-to-br from-blue-50 to-purple-50 flex items-center justify-center p-4">
                  <img src={item.img} alt={item.title} className="max-h-36 object-contain" />
                </div>
                <div className="p-6">
                  <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-500 rounded-xl flex items-center justify-center text-white font-bold text-lg">
                    {item.step}
                  </div>
                  <h3 className="mt-3 text-lg font-bold text-gray-900">{item.title}</h3>
                  <p className="mt-2 text-gray-600 text-sm">{item.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Parent Dashboard Preview */}
      <section className="py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col lg:flex-row items-center gap-12">
            <motion.div
              className="flex-1"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeIn}
            >
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">
                Parents See Everything
              </h2>
              <p className="mt-4 text-lg text-gray-600">
                Your dashboard shows every conversation, every subject, and every flag. You're always in the loop.
              </p>
              <ul className="mt-6 space-y-4">
                {[
                  { icon: <MessageSquare className="w-5 h-5 text-blue-500" />, text: 'Read full conversation transcripts' },
                  { icon: <AlertTriangle className="w-5 h-5 text-amber-500" />, text: 'Get instant alerts for flagged content' },
                  { icon: <BarChart3 className="w-5 h-5 text-purple-500" />, text: 'Track learning progress by subject' },
                  { icon: <Users className="w-5 h-5 text-green-500" />, text: 'Manage multiple child profiles' },
                  { icon: <Ban className="w-5 h-5 text-red-500" />, text: 'Set blocked keywords per child' },
                ].map((item) => (
                  <li key={item.text} className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-gray-50 rounded-xl flex items-center justify-center flex-shrink-0">
                      {item.icon}
                    </div>
                    <span className="text-gray-700">{item.text}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
            <motion.div
              className="flex-1 max-w-md w-full"
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <ParentDashboardMockup />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-14">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">Loved by Parents & Kids</h2>
            <p className="mt-4 text-lg text-gray-600">See what families are saying about Mindora</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {testimonials.map((t, i) => {
              const initials = t.name.split(' ').map(w => w[0]).join('').slice(0, 2);
              const colors = ['bg-purple-500', 'bg-blue-500', 'bg-green-500', 'bg-orange-500', 'bg-pink-500'];
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.15 }}
                  className="bg-gray-50 rounded-2xl p-6 border border-gray-100 hover:shadow-lg transition-shadow"
                >
                  <div className="flex text-yellow-400 mb-4">
                    {[...Array(t.rating || 5)].map((_, j) => (
                      <svg key={j} className="w-5 h-5 fill-current" viewBox="0 0 20 20">
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                      </svg>
                    ))}
                  </div>
                  <p className="text-gray-700 leading-relaxed mb-4 italic">"{t.quote}"</p>
                  <div className="flex items-center gap-3">
                    {t.imageUrl ? (
                      <img src={t.imageUrl} alt={t.name} className="w-10 h-10 rounded-full object-cover" />
                    ) : (
                      <div className={`w-10 h-10 ${colors[i % colors.length]} rounded-full flex items-center justify-center text-white font-bold text-sm`}>
                        {initials}
                      </div>
                    )}
                    <div>
                      <p className="font-semibold text-gray-900 text-sm">{t.name}</p>
                      <p className="text-gray-500 text-xs">{t.roleDescription}</p>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <PricingSection />

      {/* Features Grid */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-14">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">Everything a Child Needs to Learn</h2>
            <p className="mt-4 text-lg text-gray-600">Powered by AI. Controlled by parents. Loved by kids.</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                icon: <Mic className="w-7 h-7" />,
                title: 'Voice-First Learning',
                description: 'Just tap and talk. No typing needed. Perfect for young learners ages 5+ who can\'t type yet.',
              },
              {
                icon: <Globe className="w-7 h-7" />,
                title: 'Multilingual Support',
                description: 'Speaks English, Urdu, and Hindi. Mindora detects the language and responds in the same one automatically.',
              },
              {
                icon: <BookOpen className="w-7 h-7" />,
                title: 'Curriculum Aligned',
                description: 'Math, Science, English, History, and more. Content adapts to your child\'s age and grade level.',
              },
              {
                icon: <MessageSquare className="w-7 h-7" />,
                title: 'AI That Adapts',
                description: 'Vocabulary, tone, sentence length, and complexity all adjust based on your child\'s age group (5-7, 8-10, 11-13, 14+).',
              },
              {
                icon: <BarChart3 className="w-7 h-7" />,
                title: 'Progress Tracking',
                description: 'See what subjects your child explores, track mastery levels, and understand their learning patterns over time.',
              },
              {
                icon: <Shield className="w-7 h-7" />,
                title: 'Complete Privacy',
                description: 'No ads. No data selling. No third-party tracking. Your child\'s conversations are yours, not ours.',
              },
            ].map((feature, i) => (
              <motion.div
                key={feature.title}
                className="bg-white rounded-2xl p-6 border border-gray-100 hover:shadow-lg hover:border-blue-100 transition-all"
                custom={i}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeUp}
              >
                <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center text-blue-600">
                  {feature.icon}
                </div>
                <h3 className="mt-4 text-lg font-bold text-gray-900">{feature.title}</h3>
                <p className="mt-2 text-gray-600 text-sm leading-relaxed">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Subjects Strip */}
      <section className="py-16 px-4">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">Subjects Your Child Can Explore</h2>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {[
              { name: 'Math', img: '/math_bg.png' },
              { name: 'Science', img: '/science_bg.png' },
              { name: 'English', img: '/english_bg.png' },
              { name: 'General Knowledge', img: '/chat_empty.png' },
            ].map((subject, i) => (
              <motion.div
                key={subject.name}
                className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-shadow text-center border border-gray-100"
                custom={i}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeUp}
              >
                <div className="h-28 bg-gradient-to-br from-blue-50 to-purple-50 flex items-center justify-center p-3">
                  <img src={subject.img} alt={subject.name} className="max-h-24 object-contain" />
                </div>
                <div className="p-3">
                  <h3 className="font-bold text-gray-900 text-sm">{subject.name}</h3>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Age Range Callout */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-3xl p-8 sm:p-12">
            <div className="flex flex-col md:flex-row items-center gap-8">
              <div className="flex-shrink-0 flex items-center gap-4">
                <img src="/avatar-girl.png" alt="Child" className="w-20 h-20 rounded-2xl shadow-md" />
                <img src="/avatar-boy.png" alt="Child" className="w-20 h-20 rounded-2xl shadow-md" />
              </div>
              <div>
                <h3 className="text-2xl font-bold text-gray-900">Designed for Ages 5-18</h3>
                <p className="mt-3 text-gray-600 leading-relaxed">
                  Whether your child is just learning to read or preparing for high school exams, Mindora adapts. A 6-year-old gets simple sentences, fun comparisons, and a warm tone. A 13-year-old gets detailed explanations, critical thinking prompts, and academic rigor. Same AI, completely different experience.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-4 bg-gradient-to-br from-blue-600 to-purple-700">
        <div className="max-w-3xl mx-auto text-center text-white">
          <motion.div
            className="relative w-28 h-28 mx-auto mb-6"
            animate={{ y: [0, -8, 0] }}
            transition={{ repeat: Infinity, duration: 3, ease: 'easeInOut' }}
          >
            <div className="absolute inset-0 bg-white/20 rounded-3xl blur-xl" />
            <img src="/companion-sage.png" alt="Mindora AI" className="relative w-28 h-28 rounded-3xl" />
          </motion.div>
          <h2 className="text-3xl sm:text-4xl font-bold">
            Your Child Deserves an AI That's Built for Them
          </h2>
          <p className="mt-4 text-lg text-blue-100 max-w-xl mx-auto">
            Not a chatbot with a kids mode. Not ChatGPT with parental controls bolted on. A learning companion designed from the ground up for children's safety and education.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/register"
              className="px-10 py-4 text-lg font-semibold bg-white text-blue-600 rounded-2xl hover:shadow-xl transition-all"
            >
              Start Free Trial
            </Link>
            <Link
              to="/login"
              className="px-10 py-4 text-lg font-semibold border-2 border-white/30 text-white rounded-2xl hover:bg-white/10 transition-all"
            >
              Log In
            </Link>
          </div>
          <p className="mt-4 text-sm text-blue-200">Free for 10 messages/day. No credit card needed.</p>
        </div>
      </section>

      {/* Contact Us */}
      <section id="contact" className="py-20 px-4 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <motion.div
            className="text-center mb-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">Get in Touch</h2>
            <p className="mt-3 text-lg text-gray-500 max-w-xl mx-auto">
              Have a question, feedback, or partnership idea? We'd love to hear from you.
            </p>
          </motion.div>

          <div className="max-w-2xl mx-auto">
            <motion.form
              onSubmit={handleContactSubmit}
              className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8 space-y-5"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label htmlFor="contact-name" className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                  <input
                    id="contact-name"
                    type="text"
                    required
                    value={contactForm.name}
                    onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
                    placeholder="Your name"
                  />
                </div>
                <div>
                  <label htmlFor="contact-email" className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input
                    id="contact-email"
                    type="email"
                    required
                    value={contactForm.email}
                    onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
                    placeholder="you@example.com"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="contact-subject" className="block text-sm font-medium text-gray-700 mb-1">Subject</label>
                <select
                  id="contact-subject"
                  value={contactForm.subject}
                  onChange={(e) => setContactForm({ ...contactForm, subject: e.target.value })}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all bg-white"
                >
                  <option>General Inquiry</option>
                  <option>Technical Support</option>
                  <option>Billing Question</option>
                  <option>Feature Request</option>
                  <option>Partnership</option>
                  <option>Other</option>
                </select>
              </div>

              <div>
                <label htmlFor="contact-message" className="block text-sm font-medium text-gray-700 mb-1">Message</label>
                <textarea
                  id="contact-message"
                  required
                  minLength={10}
                  maxLength={2000}
                  rows={5}
                  value={contactForm.message}
                  onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all resize-none"
                  placeholder="Tell us how we can help..."
                />
              </div>

              {contactStatus && (
                <div className={`flex items-center gap-2 p-3 rounded-xl text-sm ${
                  contactStatus.type === 'success'
                    ? 'bg-green-50 text-green-700 border border-green-200'
                    : 'bg-red-50 text-red-700 border border-red-200'
                }`}>
                  {contactStatus.type === 'success' ? <CheckCircle2 className="w-4 h-4 flex-shrink-0" /> : <AlertTriangle className="w-4 h-4 flex-shrink-0" />}
                  {contactStatus.text}
                </div>
              )}

              <button
                type="submit"
                disabled={contactLoading}
                className="w-full py-3 px-6 text-white font-semibold bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl hover:shadow-lg hover:shadow-blue-500/25 transition-all disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {contactLoading ? (
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  <>
                    <Send className="w-4 h-4" />
                    Send Message
                  </>
                )}
              </button>
            </motion.form>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 bg-gray-900 text-gray-400">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-2">
              <img src="/logo.png" alt="Mindora" className="w-8 h-8 object-contain" />
              <span className="font-semibold text-white">Mindora</span>
            </div>
            <div className="flex flex-wrap justify-center gap-6 text-sm">
              <a href="#safety" className="hover:text-white transition-colors">Safety</a>
              <a href="#pricing" className="hover:text-white transition-colors">Pricing</a>
              <a href="#how-it-works" className="hover:text-white transition-colors">How It Works</a>
              <a href="#contact" className="hover:text-white transition-colors">Contact</a>
              <Link to="/login" className="hover:text-white transition-colors">Log In</Link>
              <Link to="/register" className="hover:text-white transition-colors">Sign Up</Link>
            </div>
          </div>
          <div className="mt-8 pt-8 border-t border-gray-800 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm">
            <p>&copy; {new Date().getFullYear()} Mindora. Safe AI learning for kids.</p>
            <div className="flex items-center gap-2 text-gray-500">
              <ShieldCheck className="w-4 h-4" />
              <span>COPPA Compliant</span>
              <span className="mx-1">&middot;</span>
              <Lock className="w-4 h-4" />
              <span>No Data Selling</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
